<template>
    <v-container fluid class="pa-4">
        <v-card class="mb-4">
            <v-card-title class="text-h6">
                <v-icon left color="primary">mdi-timer-check</v-icon>
                Timesheet Ticket
                <v-spacer></v-spacer>
                <v-btn color="primary" small @click="openNew">
                    <v-icon left small>mdi-plus</v-icon>
                    Nuova imputazione
                </v-btn>
                <v-btn icon class="ml-2" @click="load" :loading="loading"><v-icon>mdi-refresh</v-icon></v-btn>
            </v-card-title>

            <v-card-text>
                <v-alert v-if="!canSeeTickets" type="info" dense text class="mb-3">
                    Il modulo Ticket non è attivo o non hai il permesso di vedere i ticket: le nuove imputazioni non si possono collegare a un ticket.
                </v-alert>
                <v-row align="center" dense>
                    <v-col cols="12" md="4">
                        <v-autocomplete
                            v-model="filterEmployee"
                            :items="employees"
                            item-text="text"
                            item-value="value"
                            label="Dipendente"
                            prepend-icon="mdi-account-tie"
                            outlined
                            dense
                            hide-details
                            clearable
                            @change="load"
                        ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="3">
                        <gmh-date-picker v-model="filterFrom" label="Dal" prepend-icon="mdi-calendar-start" hide-details @change="load" editable :clearable="false"></gmh-date-picker>
                    </v-col>
                    <v-col cols="12" md="3">
                        <gmh-date-picker v-model="filterTo" label="Al" prepend-icon="mdi-calendar-end" hide-details @change="load" editable :clearable="false"></gmh-date-picker>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-data-table :headers="headers" :items="items" :loading="loading" :items-per-page="25">
                <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
                <template v-slot:item.ticket="{ item }">
                    <span v-if="item.ticket_id">
                        <v-chip x-small outlined class="mr-1">#{{ item.ticket_id }}</v-chip>
                        {{ item.ticket_title }}
                    </span>
                    <span v-else class="grey--text">—</span>
                </template>
                <template v-slot:item.hours="{ item }">{{ item.hours }}h</template>
                <template v-slot:item.hourly_cost="{ item }">{{ formatCurrency(item.hourly_cost) }}</template>
                <template v-slot:item.total_cost="{ item }">
                    <strong>{{ formatCurrency(item.total_cost) }}</strong>
                </template>
                <template v-slot:item.billable="{ item }">
                    <v-icon v-if="item.billable" color="success" small>mdi-check</v-icon>
                    <v-icon v-else color="grey" small>mdi-close</v-icon>
                </template>
            </v-data-table>
        </v-card>

        <v-card>
            <v-card-title class="text-subtitle-1">
                <v-icon left color="primary">mdi-chart-donut</v-icon>
                Report costi commessa
                <v-spacer></v-spacer>
                <v-btn small text @click="loadReport"><v-icon left small>mdi-refresh</v-icon>Ricalcola</v-btn>
            </v-card-title>
            <v-data-table
                :headers="reportHeaders"
                :items="reportRows"
                :loading="reportLoading"
                disable-pagination
                hide-default-footer
                dense>
                <template v-slot:item.project="{ item }">{{ item.project_name || '(senza progetto)' }}</template>
                <template v-slot:item.ticket="{ item }">
                    <v-chip x-small outlined class="mr-1">#{{ item.ticket_id }}</v-chip>
                    {{ item.ticket_title }}
                </template>
                <template v-slot:item.hours="{ item }">{{ parseFloat(item.hours).toFixed(2) }}h</template>
                <template v-slot:item.billable_hours="{ item }">{{ parseFloat(item.billable_hours).toFixed(2) }}h</template>
                <template v-slot:item.cost="{ item }"><strong>{{ formatCurrency(item.cost) }}</strong></template>
            </v-data-table>
        </v-card>

        <!-- Dialog nuova imputazione -->
        <v-dialog v-model="newDialog" max-width="600">
            <v-card>
                <v-card-title>
                    <v-icon left color="primary">mdi-timer-plus</v-icon>
                    Nuova imputazione ore
                </v-card-title>
                <v-card-text>
                    <v-autocomplete
                        v-model="form.employee_id"
                        :items="employees"
                        item-text="text"
                        item-value="value"
                        label="Dipendente"
                        prepend-icon="mdi-account-tie"
                        outlined
                        dense
                    ></v-autocomplete>
                    <v-autocomplete
                        v-model="form.ticket_id"
                        :items="tickets"
                        item-text="text"
                        item-value="value"
                        label="Ticket (quick-select)"
                        prepend-icon="mdi-ticket"
                        outlined
                        dense
                        clearable
                    ></v-autocomplete>
                    <v-row dense>
                        <v-col cols="12" md="4">
                            <gmh-date-picker v-model="form.date" label="Data" prepend-icon="mdi-calendar" editable></gmh-date-picker>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field v-model.number="form.hours" type="number" step="0.25" label="Ore" prepend-icon="mdi-timer" outlined dense></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-select
                                v-model="form.activity_type"
                                :items="[
                                    { value: 'work', text: 'Lavoro' },
                                    { value: 'travel', text: 'Viaggio' },
                                    { value: 'remote', text: 'Remoto' },
                                ]"
                                label="Tipo"
                                prepend-icon="mdi-tag"
                                outlined
                                dense
                            ></v-select>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12" md="8">
                            <v-textarea v-model="form.description" label="Descrizione" prepend-icon="mdi-comment-text" outlined dense rows="2" auto-grow></v-textarea>
                        </v-col>
                        <v-col cols="12" md="4" class="d-flex align-center">
                            <v-checkbox v-model="form.billable" label="Fatturabile"></v-checkbox>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="newDialog = false">Annulla</v-btn>
                    <v-btn color="primary" :loading="saving" :disabled="!formValid" @click="submitNew">Salva</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { localDateStr } from '@/components/utilities/hrDates'
    export default {
        name: 'hr-ticket-timesheets',

        data() {
            return {
                items: [],
                reportRows: [],
                employees: [],
                tickets: [],
                filterEmployee: null,
                filterFrom: this.monthStart(),
                filterTo: this.monthEnd(),
                loading: false,
                reportLoading: false,
                saving: false,
                newDialog: false,
                form: this.emptyForm(),
                headers: [
                    { text: 'Data', value: 'date' },
                    { text: 'Dipendente', value: 'employee_full_name' },
                    { text: 'Ticket', value: 'ticket' },
                    { text: 'Ore', value: 'hours' },
                    { text: 'Costo orario', value: 'hourly_cost' },
                    { text: 'Totale', value: 'total_cost' },
                    { text: 'Fatturabile', value: 'billable', align: 'center' },
                    { text: 'Descrizione', value: 'description' },
                ],
                reportHeaders: [
                    { text: 'Progetto', value: 'project' },
                    { text: 'Ticket', value: 'ticket' },
                    { text: 'Ore totali', value: 'hours' },
                    { text: 'Ore fatturabili', value: 'billable_hours' },
                    { text: 'Costo', value: 'cost' },
                ],
            }
        },

        computed: {
            formValid() { return this.form.employee_id && this.form.date && this.form.hours > 0 },
            // L'elenco dei ticket risponde 403 se il modulo Ticket e' spento o il ruolo non lo vede.
            canSeeTickets() { return this.$store.getters.checkModelPermissions('trouble') },
        },

        methods: {
            monthStart() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01` },
            monthEnd() { const d = new Date(); const last = new Date(d.getFullYear(), d.getMonth() + 1, 0); return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}` },
            emptyForm() { return { employee_id: null, ticket_id: null, date: localDateStr(), hours: 1, activity_type: 'work', billable: true, description: '' } },
            load() {
                this.loading = true
                const payload = {}
                if (this.filterEmployee) payload.subFilters = { employees: this.filterEmployee }
                if (this.filterFrom) payload.dateFrom = this.filterFrom
                if (this.filterTo) payload.dateTo = this.filterTo
                this.axios.post('hrtickettimesheets-filters', payload).then(r => {
                    const data = r.data.data && r.data.data.data ? r.data.data.data : (Array.isArray(r.data.data) ? r.data.data : [])
                    this.items = data
                    this.loading = false
                }, () => { this.loading = false })
                this.loadReport()
            },
            loadReport() {
                this.reportLoading = true
                this.axios.get(`hrtickettimesheets/costs-report?from=${this.filterFrom || ''}&to=${this.filterTo || ''}`).then(r => {
                    this.reportRows = r.data.rows || []
                    this.reportLoading = false
                }, () => { this.reportLoading = false })
            },
            loadEmployees() {
                this.axios.post('hremployees-filters').then(r => {
                    const data = r.data.data && r.data.data.data ? r.data.data.data : (Array.isArray(r.data.data) ? r.data.data : [])
                    this.employees = data.map(e => ({ value: e.id, text: `${e.first_name || ''} ${e.last_name || ''}`.trim() || `#${e.id}` }))
                })
            },
            loadTickets() {
                if (!this.canSeeTickets) return
                this.axios.post('troubles-filters').then(r => {
                    const data = r.data.data && r.data.data.data ? r.data.data.data : (Array.isArray(r.data.data) ? r.data.data : [])
                    this.tickets = data.slice(0, 100).map(t => ({ value: t.id, text: (t.code ? `[${t.code}] ` : `#${t.id} `) + (t.title || t.trouble_title || '') }))
                }, () => {})
            },
            openNew() { this.form = this.emptyForm(); this.newDialog = true },
            submitNew() {
                this.saving = true
                this.axios.post('hrtickettimesheets', this.form).then(() => {
                    this.saving = false; this.newDialog = false; this.load()
                }, () => { this.saving = false })
            },
            formatDate(d) { return d ? new Date(d).toLocaleDateString('it-IT') : '—' },
            formatCurrency(v) { return v != null ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v) : '—' },
        },

        created() {
            this.$store.commit('endProgress')
            this.loadEmployees()
            this.loadTickets()
            this.load()
        },
    }
</script>
